import { ReactNode } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface DashboardPanelProps {
  children: ReactNode;
  title?: string;
  eyebrow?: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
  contentClassName?: string;
  headerClassName?: string;
}

export function DashboardPanel({
  children,
  title,
  eyebrow,
  description,
  icon,
  action,
  className,
  contentClassName,
  headerClassName,
}: DashboardPanelProps) {
  const hasHeader = title || eyebrow || description || icon || action;

  return (
    <Card
      className={cn(
        "relative overflow-hidden border border-border/70 bg-card/95 shadow-[0_18px_40px_-24px_hsl(var(--shadow-color)/0.45)]",
        className,
      )}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/45 to-transparent" />

      {hasHeader && (
        <CardHeader className={cn("gap-4 border-b border-border/60 pb-4", headerClassName)}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              {icon && (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-primary">
                  {icon}
                </div>
              )}

              <div className="min-w-0 space-y-1">
                {eyebrow && (
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                    {eyebrow}
                  </p>
                )}
                {title && <CardTitle className="text-base font-semibold tracking-tight">{title}</CardTitle>}
                {description && <CardDescription className="text-sm leading-5">{description}</CardDescription>}
              </div>
            </div>

            {action && <div className="shrink-0">{action}</div>}
          </div>
        </CardHeader>
      )}

      <CardContent className={cn("p-5", contentClassName)}>{children}</CardContent>
    </Card>
  );
}